import { useMemo, useState } from 'react';
import type { ChartDataset } from 'chart.js';
import { X } from 'lucide-react';
import { ChartBox } from '../components/ChartBox';
import { BlockerTrendPanel } from '../components/BlockerTrendPanel';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import { buildBlockerTrend, summarizeBlockers } from '../blockerGroups';
import { loadOnlineConversationRaw } from '../api';
import type { OnlineConvRow, OnlineRawConversationResponse, OnlineRawMessage, OnlineResponse } from '../types';
import { num } from '../utils';
import { RANGE_OPTIONS } from '../ranges';

interface OnlineScreenProps {
  data: OnlineResponse | null;
  loaded: boolean;
  busy: boolean;
  filters: OnlineFilters;            // kept for signature parity (cross-filter state is internal)
  setFilters: (filters: OnlineFilters) => void;
  range: string;
  onRange: (range: string) => void;
  serverFilters: Record<string, string[]>;
  onServerFilters: (filters: Record<string, string[]>) => void;
}
export interface OnlineFilters { channel: string; intent: string; priority: string; }

interface OnlineBlockerTrendRow {
  period?: string | null;
  ai_blocker_type?: string | null;
  n?: number | string | null;
}

type OnlineResponseWithBlockerTrend = OnlineResponse & { blocker_trend?: OnlineBlockerTrendRow[] };

type Gran = 'day' | 'week' | 'month';
type Dim = 'periods' | 'channels' | 'categories' | 'subcats' | 'stages' | 'salesStages' | 'intents' | 'blockers' | 'priorities' | 'complaints' | 'groups';
type CFilters = Record<Dim, string[]>;
const EMPTY: CFilters = {
  periods: [], channels: [], categories: [], subcats: [], stages: [], salesStages: [], intents: [], blockers: [], priorities: [], complaints: [], groups: [],
};

// ---- time bucketing ----
function isoWeek(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d.getTime() - firstThu.getTime()) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
function periodLabel(code: string): string {
  if (/^\d{4}-W\d{2}$/.test(code)) return `T${code.slice(6)}/${code.slice(2, 4)}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(code)) return `${code.slice(8)}/${code.slice(5, 7)}`;
  if (/^\d{4}-\d{2}$/.test(code)) return `Th${code.slice(5)}/${code.slice(2, 4)}`;
  return code;
}


const CAT_LABEL: Record<string, string> = {
  PC: 'PC / Máy bộ', LAPTOP: 'Laptop', APPLE: 'Apple', MAN_HINH: 'Màn hình', LINH_KIEN: 'Linh kiện',
  GEAR: 'Gaming Gear', GHE_BAN: 'Ghế / Bàn', THIET_BI_MANG: 'Mạng', PHU_KIEN: 'Phụ kiện',
  VAN_PHONG: 'Văn phòng', PHAN_MEM: 'Phần mềm', DICH_VU: 'Dịch vụ', OTHER: 'Khác', UNKNOWN: 'Chưa rõ',
};
const BLOCKER_LABEL: Record<string, string> = {
  out_of_stock: 'Hết hàng', demo_unavailable: 'Thiếu demo', price_budget: 'Giá / ngân sách',
  customer_left_early: 'Khách về sớm', comparing_shops: 'So sánh nơi khác', just_browsing: 'Chỉ dạo xem',
  technical_issue: 'Vướng kỹ thuật', not_in_business: 'Không kinh doanh', internal_approval: 'Chờ duyệt nội bộ',
  waiting_response: 'Chờ phản hồi', missing_contact: 'Thiếu liên hệ', no_blocker: 'Không vướng', unknown: 'Chưa xác định',
};
const STAGE_LABEL: Record<string, string> = {
  new_consulting: 'Mới tư vấn', followup_needed: 'Cần chăm tiếp', stock_blocked: 'Vướng hàng',
  demo_blocked: 'Vướng demo', deposit_pending: 'Đã cọc', closed_won: 'Đã chốt',
  service_recheck: 'Dịch vụ', lost_unknown: 'Mất dấu',
};
const PRIO_LABEL: Record<string, string> = { p0: 'P0 · gấp', p1: 'P1 · cao', p2: 'P2 · theo dõi', p3: 'P3 · thấp' };
const INTENT_LABEL: Record<string, string> = { high: 'Cao · sẵn mua', medium: 'Trung bình', low: 'Tham khảo', service: 'Dịch vụ' };
const COMPLAINT_LABEL: Record<string, string> = { low: 'Thấp', medium: 'Trung bình', high: 'Cao' };
// Compact chart labels for the long CDP sales_stage values (full text lives in the section title).
const SALES_STAGE_SHORT: Record<string, string> = {
  'Khách tiềm năng (Có thông tin)': 'Tiềm năng',
  'Khách không tiềm năng (Không có thông tin)': 'Chưa tiềm năng',
  'Chốt thành công': 'Chốt',
};

function labelFor(dim: Dim, code: string): string {
  if (dim === 'categories') return CAT_LABEL[code] || code;
  if (dim === 'salesStages') return SALES_STAGE_SHORT[code] || code;
  if (dim === 'blockers') return BLOCKER_LABEL[code] || code;
  if (dim === 'stages') return STAGE_LABEL[code] || code;
  if (dim === 'priorities') return PRIO_LABEL[code] || code;
  if (dim === 'intents') return INTENT_LABEL[code] || code;
  if (dim === 'complaints') return COMPLAINT_LABEL[code] || code;
  if (dim === 'periods') return periodLabel(code);
  return code;
}
const DIM_TITLE: Record<Dim, string> = {
  periods: 'Kỳ', channels: 'Kênh', categories: 'Ngành hàng', subcats: 'Loại sản phẩm', stages: 'Giai đoạn',
  salesStages: 'Sales stage', intents: 'Ý định', blockers: 'Lý do chưa mua', priorities: 'Ưu tiên',
  complaints: 'Khiếu nại', groups: 'Nhóm',
};

function entries(rows: Array<Record<string, number | string | null>> | undefined, key: string, value: string): Array<[string, number]> {
  const m = new Map<string, number>();
  (rows || []).forEach(row => {
    const k = String(row[key] || '');
    if (!k) return;
    m.set(k, (m.get(k) || 0) + Number(row[value] || 0));
  });
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}
function barDS(entries: Array<[string, number]>, selected: string[], base: string): ChartDataset[] {
  const sel = new Set(selected);
  return [{
    label: 'Số hội thoại', data: entries.map(e => e[1]), type: 'bar',
    backgroundColor: entries.map(e => (sel.size === 0 || sel.has(e[0])) ? `${base}cc` : `${base}33`),
    borderColor: base, borderWidth: 1, borderRadius: 5, maxBarThickness: 56,
  } as unknown as ChartDataset];
}

// Right-size chart height to content so low-cardinality charts don't waste space:
// vertical bars need a fixed-ish height (few categories → compact); horizontal bars grow per bar.
const vH = (n: number) => (n <= 5 ? 200 : 250);
const hH = (n: number) => Math.min(340, Math.max(140, n * 26 + 60));

interface ProdAgg { name: string; category: string; count: number; warm: number; closed: number; complaint: number; }

interface RawConversationState {
  open: boolean;
  loading: boolean;
  data?: OnlineRawConversationResponse;
  error?: string;
}


export function OnlineScreen({ data, loaded, busy, range, onRange, serverFilters, onServerFilters }: OnlineScreenProps) {
  const [filters, setFilters] = useState<CFilters>({ ...EMPTY, ...serverFilters });
  const [gran, setGran] = useState<Gran>('day');
  const [rawConversations, setRawConversations] = useState<Record<string, RawConversationState>>({});
  const rows = data?.conversations || [];
  const detailTotal = Number(data?.meta?.detail_total || data?.dashboard?.conversations || rows.length);

  const toggle = (dim: Dim, value: string) => {
    if (!value || dim === 'periods') return;
    setFilters(prev => {
      const cur = prev[dim];
      const next = { ...prev, [dim]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] };
      onServerFilters(next);
      return next;
    });
  };
  const clearAll = () => { setFilters(EMPTY); onServerFilters(EMPTY); };
  const setGranularity = (g: Gran) => { setGran(g); setFilters(prev => ({ ...prev, periods: [] })); };
  const changeRange = (v: string) => { setFilters(EMPTY); onRange(v); };
  const activeCount = (Object.keys(filters) as Dim[]).reduce((n, d) => n + filters[d].length, 0);

  const loadRawConversation = async (conversationId: string) => {
    setRawConversations(prev => ({ ...prev, [conversationId]: { open: true, loading: true } }));
    try {
      const raw = await loadOnlineConversationRaw(conversationId);
      setRawConversations(prev => ({
        ...prev,
        [conversationId]: { open: prev[conversationId]?.open ?? true, loading: false, data: raw },
      }));
    } catch (error) {
      setRawConversations(prev => ({
        ...prev,
        [conversationId]: {
          open: prev[conversationId]?.open ?? true,
          loading: false,
          error: String((error as Error)?.message || error),
        },
      }));
    }
  };

  const toggleRawConversation = async (conversationId: string) => {
    const current = rawConversations[conversationId];
    if (current?.open) {
      setRawConversations(prev => ({ ...prev, [conversationId]: { ...prev[conversationId], open: false } }));
      return;
    }
    if (current?.data) {
      setRawConversations(prev => ({ ...prev, [conversationId]: { ...prev[conversationId], open: true } }));
      return;
    }
    if (current?.loading) return;
    await loadRawConversation(conversationId);
  };


  const filtered = rows;
  const total = Number(data?.dashboard?.conversations || rows.length);
  const warm = Number(data?.dashboard?.warm_leads || 0);
  const highValue = Number(data?.dashboard?.high_value || 0);
  const urgent = Number(data?.dashboard?.urgent_followup || 0);
  const complaints = Number(data?.dashboard?.complaints || 0);
  const won = Number(data?.dashboard?.sales_stage_won || data?.dashboard?.closed_won || 0);
  const phoneKnown = Number(data?.dashboard?.phone_known || 0);
  const pct = (n: number) => total ? `${Math.round((n / total) * 100)}%` : '0%';

  const periodEntries = useMemo(() => {
    const entries = (data?.periods || []).map(r => [String(r.period || ''), { n: Number(r.n || 0), warm: Number(r.warm || 0), comp: Number(r.complaints || 0) }] as [string, { n: number; warm: number; comp: number }]);
    if (gran === 'day') return entries;
    const m = new Map<string, { n: number; warm: number; comp: number }>();
    entries.forEach(([day, v]) => {
      const k = gran === 'month' ? day.slice(0, 7) : isoWeek(day);
      const e = m.get(k) || { n: 0, warm: 0, comp: 0 };
      e.n += v.n; e.warm += v.warm; e.comp += v.comp;
      m.set(k, e);
    });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [data?.periods, gran]);

  const channelEntries = entries(data?.channels, 'channel', 'conversations');
  const catEntries = entries(data?.categories, 'ai_category', 'n');
  const stageEntries = entries(data?.deal_stages, 'ai_deal_stage', 'n');
  const salesStageEntries = entries(data?.sales_stages, 'sales_stage', 'n');
  const intentEntries = entries(data?.intents, 'ai_purchase_intent', 'n');
  const blockerEntries = entries(data?.blockers, 'ai_blocker_type', 'n');
  const subcatEntries = entries(data?.subcats, 'ai_subcategory_name', 'n').slice(0, 14);

  const blockerTrendData = useMemo(() => {
    const trendRows = ((data as OnlineResponseWithBlockerTrend | null)?.blocker_trend || []).map(row => ({
      period: String(row.period || ''),
      blocker: row.ai_blocker_type,
      count: Number(row.n || 0),
    }));
    const summaryRows = (data?.blockers || []).map(row => ({
      period: '',
      blocker: String(row.ai_blocker_type || ''),
      count: Number(row.n || 0),
    }));
    const bucket = (period: string) => gran === 'month' ? period.slice(0, 7) : gran === 'week' ? isoWeek(period) : period;
    return {
      points: buildBlockerTrend(trendRows, bucket),
      summary: summarizeBlockers(summaryRows),
    };
  }, [data, gran]);

  const products = useMemo(() => {
    return (data?.products || []).map(p => ({
      name: String(p.product_label || p.product || ''),
      category: '',
      count: Number(p.mentions || 0),
      warm: Number(p.warm_leads || 0),
      closed: Number(p.sales_stage_won || 0),
      complaint: 0,
    })).filter(p => p.name);
  }, [data?.products]);

  const groupAgg = useMemo(() => (data?.groups || []).map(g => ({
    group: String(g.group_name || ''),
    agent: String(g.agent_name || ''),
    n: Number(g.conversations || 0),
    warm: Number(g.warm_leads || 0),
    closed: Number(g.sales_stage_won || 0),
    complaint: Number(g.complaints || 0),
    urgent: Number(g.urgent_followup || 0),
  })).filter(g => g.group || g.agent), [data?.groups]);

  const latestVn = data?.latest?.max_start_vn ? data.latest.max_start_vn.replace(/^(\d{4})-(\d{2})-(\d{2}) /, '$3/$2 ') : '—';
  const latestEnriched = data?.latest?.max_enriched_at ? new Date(data.latest.max_enriched_at).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  if (busy && !loaded) return <div className="card empty">Đang tải dữ liệu tư vấn online...</div>;
  const empty = loaded && rows.length === 0;

  return (
    <>
      <section className="card space" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <b>Khoảng thời gian</b>
        <div className="seg">
          {RANGE_OPTIONS.map(o => (
            <button key={o.v} type="button" className={`tab ${range === o.v ? 'active' : ''}`} onClick={() => changeRange(o.v)}>{o.l}</button>
          ))}
        </div>
        <span className="muted">· {num(total)} hội thoại trong khoảng</span>
        <div className="status-strip" style={{ marginLeft: 'auto' }}>
          <span><b>Dữ liệu chat tới:</b> {latestVn}</span>
          <span><b>Cập nhật kho:</b> {latestEnriched}</span>
          <span className="muted">Nguồn CDP đổ theo lô hằng ngày</span>
        </div>
      </section>
      {empty && <div className="card empty space">Chưa có hội thoại online trong khoảng này. Dữ liệu online về theo lô 1 lần/ngày (khoảng 15h–20h) nên "Hôm nay" thường trống tới chiều — chọn "Hôm qua" hoặc "7 ngày".</div>}
      {!empty && (<>

      <section className="grid kpis">
        <KpiCard label="Hội thoại" value={num(total)} sub={`${num(rows.length)} dòng chi tiết đã tải / ${num(detailTotal)} · Zalo/FB/Livechat`} tone="blue" />
        <KpiCard label="Khách quan tâm cao" value={pct(warm)} sub={`${num(warm)} hội thoại có ý định mua cao hoặc trung bình`} tone="green" />
        <KpiCard label="Cần ưu tiên" value={num(urgent)} sub="Mức gấp hoặc cao" tone="amber" />
        <KpiCard label="Khách giá trị cao" value={num(highValue)} sub="Theo nhu cầu và ngành hàng" tone="violet" />
        <KpiCard label="Khiếu nại" value={num(complaints)} sub="Hội thoại có phản ánh" tone="red" />
        <KpiCard label="Đã chốt" value={num(won)} sub="Hội thoại có kết quả chốt thành công" tone="green" />
        <KpiCard label="Biết SĐT" value={num(phoneKnown)} sub="có hash SĐT" tone="cyan" />
        <KpiCard label="Sản phẩm được hỏi" value={num(products.length)} sub="lineup khách quan tâm" tone="gray" />
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Bộ lọc chéo</h2>
          <span>Bấm cột trong biểu đồ để lọc · {activeCount ? `${activeCount} bộ lọc đang bật` : 'chưa lọc'}</span>
        </div>
        <div className="filter-pills">
          {activeCount === 0 && <span className="muted">Bấm cột bất kỳ (kênh, ngành hàng, giai đoạn, ý định…) để lọc lại server.</span>}
          {(Object.keys(filters) as Dim[]).flatMap(dim => filters[dim].map(val => (
            <button key={`${dim}:${val}`} type="button" className="pill filter" onClick={() => toggle(dim, val)}>
              <span className="muted">{DIM_TITLE[dim]}:</span> {labelFor(dim, val)} <X size={12} />
            </button>
          )))}
          {activeCount > 0 && <button type="button" className="pill ghost" onClick={clearAll}>Xóa hết</button>}
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Xu hướng hội thoại</h2>
          <div className="seg">
            {(['day', 'week', 'month'] as Gran[]).map(g => (
              <button key={g} type="button" className={`tab ${gran === g ? 'active' : ''}`} onClick={() => setGranularity(g)}>
                {g === 'day' ? 'Ngày' : g === 'week' ? 'Tuần' : 'Tháng'}
              </button>
            ))}
          </div>
        </div>
        <div className="chart tall">
          <ChartBox
            labels={periodEntries.map(e => periodLabel(e[0]))}
            datasets={[
              ...barDS(periodEntries.map(e => [e[0], e[1].n]), filters.periods, '#2563eb'),
              { label: 'Lead ấm', data: periodEntries.map(e => e[1].warm), type: 'line', borderColor: '#16a34a', backgroundColor: '#16a34a22', borderWidth: 2, tension: 0.35, pointRadius: 3, yAxisID: 'y' } as unknown as ChartDataset,
              { label: 'Khiếu nại', data: periodEntries.map(e => e[1].comp), type: 'line', borderColor: '#dc2626', backgroundColor: '#dc262622', borderWidth: 2, borderDash: [5, 3], tension: 0.35, pointRadius: 3, yAxisID: 'y' } as unknown as ChartDataset,
            ]}
          />
        </div>
      </section>

      <BlockerTrendPanel
        points={blockerTrendData.points}
        summary={blockerTrendData.summary}
        periodLabel={periodLabel}
      />

      {/* compact 3-up: low-cardinality breakdowns don't need full-height cards */}
      <section className="grid three space atop">
        <div className="card">
          <div className="section-title"><h2>Kênh</h2><span>bấm để lọc</span></div>
          <div className="chart" style={{ height: vH(channelEntries.length) }}><ChartBox labels={channelEntries.map(e => e[0])} datasets={barDS(channelEntries, filters.channels, '#0891b2')} options={{ plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('channels', channelEntries[index][0])} /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Ý định mua</h2><span>bấm để lọc</span></div>
          <div className="chart" style={{ height: vH(intentEntries.length) }}><ChartBox labels={intentEntries.map(e => labelFor('intents', e[0]))} datasets={barDS(intentEntries, filters.intents, '#16a34a')} options={{ plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('intents', intentEntries[index][0])} /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Phễu tư vấn</h2><span>Giai đoạn hội thoại · bấm để lọc</span></div>
          <div className="chart" style={{ height: vH(salesStageEntries.length) }}><ChartBox labels={salesStageEntries.map(e => labelFor('salesStages', e[0]))} datasets={barDS(salesStageEntries, filters.salesStages, '#6d28d9')} options={{ indexAxis: 'y', plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('salesStages', salesStageEntries[index][0])} /></div>
        </div>
      </section>

      {/* higher-cardinality vertical breakdowns — wider column to the one with more bars */}
      <section className="grid split space atop">
        <div className="card">
          <div className="section-title"><h2>Ngành hàng</h2><span>bấm để lọc</span></div>
          <div className="chart" style={{ height: vH(catEntries.length) }}><ChartBox labels={catEntries.map(e => labelFor('categories', e[0]))} datasets={barDS(catEntries, filters.categories, '#2563eb')} options={{ plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('categories', catEntries[index][0])} /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Giai đoạn tư vấn</h2><span>bấm để lọc</span></div>
          <div className="chart" style={{ height: vH(stageEntries.length) }}><ChartBox labels={stageEntries.map(e => labelFor('stages', e[0]))} datasets={barDS(stageEntries, filters.stages, '#7c3aed')} options={{ plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('stages', stageEntries[index][0])} /></div>
        </div>
      </section>

      {/* blockers — horizontal bars, height scales with the number of reasons */}
      <section className="card space">
        <div className="section-title"><h2>Lý do khách chưa mua</h2><span>bấm để lọc</span></div>
        <div className="chart" style={{ height: hH(blockerEntries.length) }}><ChartBox labels={blockerEntries.map(e => labelFor('blockers', e[0]))} datasets={barDS(blockerEntries, filters.blockers, '#b45309')} options={{ indexAxis: 'y', plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('blockers', blockerEntries[index][0])} /></div>
      </section>

      <section className="card space" style={{ borderTop: '3px solid var(--cyan)' }}>
        <div className="section-title">
          <h2>Sản phẩm khách hỏi — tối ưu lineup</h2>
          <span>Tổng hợp từ nội dung hội thoại · theo bộ lọc hiện tại</span>
        </div>
        <div className="grid split">
          <div>
            <div className="section-title"><h2>Loại sản phẩm</h2><span>bấm để lọc</span></div>
            <div className="chart" style={{ height: hH(subcatEntries.length) }}><ChartBox labels={subcatEntries.map(e => e[0])} datasets={barDS(subcatEntries, filters.subcats, '#16a34a')} options={{ indexAxis: 'y', plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('subcats', subcatEntries[index][0])} /></div>
          </div>
          <div>
            <div className="section-title"><h2>Top sản phẩm</h2><span>{num(products.length)} sản phẩm</span></div>
            <div className="scroll" style={{ maxHeight: 320 }}>
              <table>
                <thead><tr><th>Sản phẩm</th><th className="right">Lượt hỏi</th><th className="right">Lead ấm</th><th className="right">Đã chốt</th></tr></thead>
                <tbody>{products.slice(0, 40).map((p, i) => (
                  <tr key={`${p.name}-${i}`}>
                    <td>{p.name}</td>
                    <td className="right tnum"><b>{num(p.count)}</b></td>
                    <td className="right tnum">{p.warm || '—'}</td>
                    <td className="right tnum">{p.closed ? <Tag tone="green">{p.closed}</Tag> : '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="card space">
        <div className="section-title"><h2>Năng suất theo nhóm / nhân viên</h2><span>{num(groupAgg.length)} NV · theo bộ lọc</span></div>
        <div className="scroll" style={{ maxHeight: 360 }}>
          <table>
            <thead><tr><th>Nhóm</th><th>Nhân viên</th><th className="right">Hội thoại</th><th className="right">Lead ấm</th><th className="right">Chốt</th><th className="right">Khiếu nại</th><th className="right">Gấp</th></tr></thead>
            <tbody>{groupAgg.slice(0, 100).map((g, i) => (
              <tr key={`${g.group}-${g.agent}-${i}`}>
                <td className="muted">{g.group || '—'}</td>
                <td><b>{g.agent || '—'}</b></td>
                <td className="right tnum">{num(g.n)}</td>
                <td className="right tnum">{g.warm || '—'}</td>
                <td className="right tnum">{g.closed ? <Tag tone="green">{g.closed}</Tag> : '—'}</td>
                <td className="right tnum">{g.complaint ? <Tag tone="red">{g.complaint}</Tag> : '—'}</td>
                <td className="right tnum">{g.urgent || '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Chi tiết hội thoại</h2>
          <span>{num(filtered.length)} dòng (theo bộ lọc) · SĐT đã ẩn</span>
        </div>
        <div className="scroll">
          <table>
            <thead><tr><th>Ngày</th><th>Kênh</th><th>NV</th><th>Ngành hàng</th><th>Giai đoạn</th><th>Ý định</th><th>Ưu tiên</th><th>Khiếu nại</th><th>Trao đổi gốc (đã ẩn SĐT và email)</th></tr></thead>
            <tbody>{filtered.slice(0, 250).map(r => {
              const raw = rawConversations[r.conversation_id];
              const rawPanelId = `online-raw-${r.conversation_id.replace(/[^A-Za-z0-9_-]/g, '-')}`;
              return (
              <tr key={r.conversation_id}>
                <td className="tnum muted">{r.day}</td>
                <td><b>{r.channel}</b></td>
                <td>{r.agent_name || '—'}</td>
                <td><Tag tone={r.ai_category === 'UNKNOWN' ? 'gray' : 'blue'}>{labelFor('categories', r.ai_category || '')}</Tag></td>
                <td>{r.ai_deal_stage ? labelFor('stages', r.ai_deal_stage) : '—'}</td>
                <td><Tag tone={r.ai_purchase_intent === 'high' ? 'green' : r.ai_purchase_intent === 'medium' ? 'amber' : r.ai_purchase_intent === 'service' ? 'blue' : 'gray'}>{r.ai_purchase_intent ? labelFor('intents', r.ai_purchase_intent) : '—'}</Tag></td>
                <td>{r.ai_followup_priority ? <Tag tone={r.ai_followup_priority === 'p0' ? 'red' : r.ai_followup_priority === 'p1' ? 'amber' : 'gray'}>{PRIO_LABEL[r.ai_followup_priority] || r.ai_followup_priority}</Tag> : '—'}</td>
                <td>{r.complaint_level ? <Tag tone={r.complaint_level === 'high' ? 'red' : 'amber'}>{labelFor('complaints', r.complaint_level)}</Tag> : '—'}</td>
                <td className="need online-raw-cell">
                  <button
                    type="button"
                    className="online-raw-toggle"
                    aria-expanded={Boolean(raw?.open)}
                    aria-controls={rawPanelId}
                    onClick={() => { void toggleRawConversation(r.conversation_id); }}
                  >
                    {raw?.open ? 'Ẩn trao đổi gốc' : 'Xem trao đổi gốc'}
                  </button>
                  {raw?.open && (
                    <div className="online-raw-panel" id={rawPanelId}>
                      {raw.loading && <span className="muted">Đang tải trao đổi gốc...</span>}
                      {raw.error && (
                        <div className="online-raw-error">
                          <span>{raw.error}</span>
                          <button type="button" onClick={() => { void loadRawConversation(r.conversation_id); }}>Thử lại</button>
                        </div>
                      )}
                      {raw.data && <RawConversation data={raw.data} />}
                    </div>
                  )}
                </td>
              </tr>
            );})}</tbody>
          </table>
        </div>
      </section>
      </>)}
    </>
  );
}

function RawConversation({ data }: { data: OnlineRawConversationResponse }) {
  return (
    <div className="online-raw-transcript">
      <div className="online-raw-summary">
        <span>{num(data.returned_messages)} / {num(data.total_messages)} tin nhắn</span>
        {data.truncated && <b>Chỉ hiển thị 500 tin đầu tiên</b>}
      </div>
      {data.messages.length === 0 && <span className="muted">Hội thoại chưa có tin nhắn raw.</span>}
      {data.messages.map(message => <RawMessage key={message.message_id} message={message} />)}
    </div>
  );
}

function RawMessage({ message }: { message: OnlineRawMessage }) {
  const roleLabel = message.role === 'agent'
    ? 'NV'
    : message.role === 'visitor' ? 'Khách' : message.role === 'system' ? 'Hệ thống' : 'Không rõ';
  return (
    <div className={`online-raw-message ${message.role}`}>
      <div className="online-raw-meta">
        <b>{roleLabel}{message.speaker ? ` · ${message.speaker}` : ''}</b>
        <span>{message.event_time || 'Không có thời gian'} · type {message.message_type}</span>
      </div>
      <div className="online-raw-text">{message.text || '—'}</div>
    </div>
  );
}

